import * as React from "react";
import { styled } from "../../../../styled-components";
import { NavigationAnimation } from "../../../../lib/navigation-model";

const Wrapper = styled.div`
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;

    will-change: transform, opacity;

    &.screenPushIn {
        @keyframes pushIn {
            0% {
                transform: translateX(100%);
            }
            100% {
                transform: translateX(0);
            }
        }
        animation: 0.25s ease-out 0s 1 pushIn;
    }
    &.screenPushOut {
        @keyframes pushOut {
            0% {
                transform: translateX(0);
                opacity: 1;
            }
            100% {
                transform: translateX(-25%);
                opacity: 0.8;
            }
        }
        animation: 0.25s ease-out 0s 1 pushOut;
    }
    &.screenPopIn {
        @keyframes popIn {
            0% {
                transform: translateX(-25%);
                opacity: 0.8;
            }
            100% {
                transform: translateX(0);
                opacity: 1;
            }
        }
        animation: 0.25s ease-out 0s 1 popIn;
    }
    &.screenPopOut {
        @keyframes popOut {
            0% {
                transform: translateX(0);
            }
            100% {
                transform: translateX(100%);
            }
        }
        animation: 0.25s ease-out 0s 1 popOut;
    }

    &.screenPushModalIn {
        @keyframes pushInModal {
            0% {
                transform: translateY(100%);
            }
            100% {
                transform: translateY(0);
            }
        }
        animation: 0.25s ease-out 0s 1 pushInModal;
    }
    &.screenPushModalOut {
        @keyframes pushOutModal {
            0% {
                opacity: 1;
            }
            100% {
                opacity: 0.999;
            }
        }
        animation: 0.25s ease-out 0s 1 pushOutModal;
    }
    &.screenPopModalIn {
        @keyframes popInModal {
            0% {
                opacity: 0.999;
            }
            100% {
                opacity: 1;
            }
        }
        animation: 0.25s ease-out 0s 1 popInModal;
    }
    &.screenPopModalOut {
        @keyframes popOutModal {
            0% {
                transform: translateY(0);
            }
            100% {
                transform: translateY(100%);
            }
        }
        animation: 0.25s ease-out 0s 1 popOutModal;
    }

    &.dragFinish {
        transition: transform 0.15s, opacity 0.15s;
    }
`;

interface Props {
    readonly present: boolean;
    readonly animation: NavigationAnimation;
    readonly onHidden: (() => void) | undefined;
    readonly dragOffset: number | undefined;
    readonly dragOpacity: number | undefined;
    readonly dragFinishing: boolean;
    readonly noScroll: boolean;
    readonly forceRender: boolean;
}

// TODO: Is this doing anything useful anymore?
interface State {
    readonly hide: boolean;
}

export default class ScreenAnimator extends React.PureComponent<Props, State> {
    public state: State = {
        hide: false,
    };

    public static getDerivedStateFromProps(props: Props, state: State) {
        if (state.hide && props.present) {
            return {
                ...state,
                hide: false,
            };
        }

        return state;
    }

    private onAnimationEnd = (event: React.AnimationEvent<HTMLDivElement> | React.TransitionEvent<HTMLDivElement>) => {
        if (this.props.present || event.currentTarget !== event.target) return;

        this.setState({ hide: true });
        if (this.props.onHidden !== undefined) {
            this.props.onHidden();
        }
    };

    public render(): React.ReactNode {
        const {
            children,
            present,
            animation,
            dragOffset,
            dragOpacity,
            dragFinishing,
            noScroll,
            forceRender,
        } = this.props;
        const { hide } = this.state;

        if (hide && !forceRender) return null;

        let style: React.CSSProperties | undefined;
        if (dragOffset !== undefined || dragOpacity !== undefined) {
            style = {};
            if (dragOpacity !== undefined) style.opacity = dragOpacity;
            if (dragOffset !== undefined) style.transform = `translate(${dragOffset}%)`;
        }

        let className = noScroll ? "noScroll" : "";
        if (dragFinishing) {
            className = "dragFinish";
        } else if (animation === "push") {
            className = present ? "screenPushIn" : "screenPushOut";
        } else if (animation === "pop") {
            className = present ? "screenPopIn" : "screenPopOut";
        } else if (animation === "push-modal") {
            className = present ? "screenPushModalIn" : "screenPushModalOut";
        } else if (animation === "pop-modal") {
            className = present ? "screenPopModalIn" : "screenPopModalOut";
        }

        return (
            <Wrapper
                style={style}
                onAnimationEnd={this.onAnimationEnd}
                onTransitionEnd={this.onAnimationEnd}
                className={className}>
                {children}
            </Wrapper>
        );
    }
}
